/**
 * Reconhecer que o atendimento acabou, do lado do cliente.
 *
 * ## Por que isto é um módulo
 *
 * Porque é a decisão que dispara TUDO o que acontece no fim: trava o campo de
 * mensagem, limpa a sessão e abre a avaliação. Errar para menos deixa o
 * cliente num chat morto achando que ainda é atendido; errar para mais encerra
 * uma conversa viva.
 *
 * E porque já errou. A regra comparava a mensagem com o NOME do operador da
 * sala: quando OUTRO operador encerrava, o texto não batia, e o chat não era
 * finalizado nem bloqueado para o cliente.
 *
 * ## E errou de novo, pelos dois lados, adivinhando TEXTO (03/11/2026)
 *
 * Reconhecer o fim de uma conversa lendo as palavras dela tem dois furos, e os
 * dois estavam abertos:
 *
 * - **para mais:** o aviso de inatividade diz "esta conversa será ENCERRADA em
 *   N minuto(s)". A palavra batia, e o cliente que ainda estava sendo atendido
 *   -- que recebia o aviso justamente para voltar a responder -- perdia o
 *   campo de mensagem e caía na avaliação em 8 segundos. O aviso expulsava
 *   quem ele existia para trazer de volta. Estava invisível só porque o
 *   encerramento automático está desligado em produção;
 * - **para menos:** as caixas de Configurações são texto livre. "Até breve!"
 *   não tem "encerrad" nenhum, e a segunda pista compara com a mensagem de
 *   encerramento NORMAL -- que é outra caixa, e que o cliente só conhece se
 *   algum operador tiver falado com ele antes.
 *
 * Quem sabe o que cada mensagem é é o SERVIDOR, que a escreveu. Agora ele diz,
 * em `meta`, e a marca manda nos dois sentidos. As pistas de texto continuam
 * aqui para o histórico já gravado, que não tem marca nenhuma.
 */

/**
 * A marca do servidor, quando houver: `true` encerra, `false` não encerra,
 * `null` quando ele não disse nada.
 *
 * Os dois sentidos importam. Dizer "é encerramento" resolve o texto livre;
 * dizer "NÃO é" é o que impede o aviso de inatividade de derrubar uma conversa
 * viva, e nenhuma regra de palavra consegue fazer isso com texto configurável.
 */
const marcaDoServidor = (mensagem) => {
    const meta = mensagem && mensagem.meta;

    // Sem `typeof` nenhum: `meta` que não seja objeto -- um texto, um número --
    // simplesmente não tem estes campos, e ler `undefined` deles já cai no
    // `null` do fim. A guarda só precisa cobrir `null`/ausente, que estouraria.
    if (!meta) return null;

    if (meta.encerramento === true) return true;
    if (meta.aviso_de_inatividade === true) return false;

    return null;
};

/**
 * A última mensagem é a de encerramento?
 *
 * Só mensagem automática (`type 4`) da sala atual conta. A fala do operador
 * (`type 1`) pode dizer "vou encerrar" sem encerrar nada, e mensagem de outra
 * sala não fala desta conversa.
 *
 * A MARCA do servidor decide quando ela existe (ver `marcaDoServidor`). Sem
 * marca -- o histórico gravado antes de 03/11/2026 --, valem as pistas de
 * texto: "encerrad" (pega "encerrada" e "encerrado") ou a mensagem de
 * encerramento configurada no departamento. Os dois caminhos de texto existem
 * porque a mensagem configurada é livre: o administrador pode escrever
 * "Obrigado pelo contato!", que não contém "encerrad" nenhum.
 *
 * @param {object} ultimaMensagem
 * @param {string|number} roomId   a sala aberta agora
 * @param {string} textoConfigurado  `roomInfo.closing_message`
 */
export const ehMensagemDeEncerramento = (ultimaMensagem, roomId, textoConfigurado) => {
    if (!ultimaMensagem || ultimaMensagem.type !== 4) return false;

    // Comparado como TEXTO: o id da sala vem de lugares diferentes (socket,
    // estado, url) e nem sempre no mesmo tipo.
    if (String(ultimaMensagem.room_id) !== String(roomId)) return false;

    // A marca do servidor vem DEPOIS das duas guardas acima, e não antes: ela
    // diz o que a mensagem é, não de qual sala ela é. Mensagem marcada de
    // OUTRA sala não encerra esta.
    const marcada = marcaDoServidor(ultimaMensagem);
    if (marcada !== null) return marcada;

    const texto = String(ultimaMensagem.message || '').toLowerCase();
    const configurado = String(textoConfigurado || '').toLowerCase().trim();

    if (/encerrad[oa]/.test(texto)) return true;

    return configurado !== '' && texto.includes(configurado);
};
